import { MapLayerCategory } from "../../../utils/layer-registry";
import { darkenColor } from "./BaseMapDialogHelpers";
import {
  getOverlayLineStyles,
  lineOpacityFor,
  overlayLineTypeForSource,
  scaleLineWidth,
} from "../../../utils/overlayLineStyles";

/** The authored (1×, 100%) widths for a personal-trail overlay. */
const BASE_MAIN_WIDTH: any[] = ['interpolate', ['linear'], ['zoom'], 0, 1, 14, 4];
const BASE_HALO_WIDTH = 6;

/**
 * Resolves the user's customised line style for a personal-trail source. Falls
 * back to the caller-supplied colour (the store's `sourceColors`) so a source
 * that isn't a customisable line overlay keeps rendering exactly as before.
 */
export function overlayLinePaint(src: string, fallbackColor?: string) {
  const type = overlayLineTypeForSource(src);
  const prefs = type ? getOverlayLineStyles()[type] : null;
  const strokeColor = prefs?.color || fallbackColor || '#000000';
  const multiplier = prefs?.thickness ?? 1;
  return {
    strokeColor,
    haloColor: darkenColor(strokeColor),
    opacity: prefs ? lineOpacityFor(prefs) : 1,
    mainWidth: scaleLineWidth(BASE_MAIN_WIDTH, multiplier),
    haloWidth: scaleLineWidth(BASE_HALO_WIDTH, multiplier),
  };
}

export class LineSourceHelper {
  static toggle(src: string, isActive: boolean, minZoom: number, color?: string) {
    if (!window.map || !window.layerRegistry || !window.user) {
      console.error('LineSourceHelper.toggle ➜ map, registry, or user missing');
      return;
    }

    const layerId = src; // main coloured stroke
    const borderId = `${src}-border`;
    const namesId = `${src}-names`;

    if (isActive) {
      const sourceSpec = this.buildVectorSourceSpec(src, minZoom);
      const paint = overlayLinePaint(src, color);

      // Main line (z:1)
      window.layerRegistry.addLayer(
        {
          id: layerId,
          type: 'line',
          source: src,
          'source-layer': 'data',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': paint.strokeColor,
            'line-width': paint.mainWidth,
            'line-opacity': paint.opacity
          }
        },
        MapLayerCategory.Annotations,
        {
          z: 1,
          // @ts-ignore
          source: { id: src, spec: sourceSpec }
        }
      );

      // Halo (z:0)
      window.layerRegistry.addLayer(
        {
          id: borderId,
          type: 'line',
          source: src,
          'source-layer': 'data',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': paint.haloColor,
            'line-width': paint.haloWidth,
            'line-opacity': paint.opacity
          }
        },
        MapLayerCategory.Annotations,
        { z: 0 }
      );

      // Trail names (z:2)
      window.layerRegistry.addLayer(
        {
          id: namesId,
          type: 'symbol',
          source: src,
          'source-layer': 'data',
          minzoom: 9,
          layout: {
            'symbol-placement': 'line',
            'text-field': ['get', 'title'],
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-size': 12,
            'text-allow-overlap': false,
            'symbol-spacing': 250,
            'text-max-angle': 30,
            'text-pitch-alignment': 'map',
            'text-ignore-placement': false
          },
          paint: {
            'text-color': '#333333',
            'text-halo-color': 'rgba(255, 255, 255, 0.8)',
            'text-halo-width': 1.5
          }
        },
        MapLayerCategory.Annotations,
        { z: 2 }
      );
    } else {
      // Remove in reverse-ish order; registry handles orphaned sources
      window.layerRegistry.removeLayer(borderId);
      window.layerRegistry.removeLayer(layerId);
      window.layerRegistry.removeLayer(namesId);
    }
  }

  /**
   * Re-applies the current line style to an already-added source without
   * tearing the layers (and their tiles) down. Called when the user edits the
   * style, or when a value arrives from another platform via cloud sync.
   */
  static restyle(src: string, fallbackColor?: string) {
    const map: any = window.map;
    if (!map || typeof map.getLayer !== 'function') return;

    const paint = overlayLinePaint(src, fallbackColor);
    const borderId = `${src}-border`;

    if (map.getLayer(src)) {
      map.setPaintProperty(src, 'line-color', paint.strokeColor);
      map.setPaintProperty(src, 'line-width', paint.mainWidth);
      map.setPaintProperty(src, 'line-opacity', paint.opacity);
    }
    if (map.getLayer(borderId)) {
      map.setPaintProperty(borderId, 'line-color', paint.haloColor);
      map.setPaintProperty(borderId, 'line-width', paint.haloWidth);
      map.setPaintProperty(borderId, 'line-opacity', paint.opacity);
    }
  }

  private static buildVectorSourceSpec(src: string, minZoom: number) {
    const baseUrl = window.location.origin;
    const tilesUrl = `${baseUrl}/vector/${src}/{z}/{x}/{y}?public_filter=false&user_id=${window.user.id}&t=${Date.now()}`;
    return {
      type: 'vector' as const,
      tiles: [tilesUrl],
      minzoom: minZoom,
      maxzoom: 14
    };
  }
}
